import { create } from 'zustand';
import {
  defaultEditorState,
  languageForParam,
  type EditorLanguage,
  type EditorState,
  type HeaderRow,
  type PayloadParam,
} from '@xsscool/shared';
import {
  loadFromStorage,
  loadHydratedShare,
  loadTabFromStorage,
  loadDomainFromStorage,
  saveDomainToStorage,
  type HydratedShare,
} from './persist';

export type EditorTab = 'body' | 'headers' | 'captures';

// The share the editor currently has open. Null means a scratch document:
// Save creates a new share. Non-null means Save updates that share in place.
// Only shares the caller owns are ever "open"; opening someone else's
// /e/<id> loads the content as scratch so Save forks it.
export interface CurrentShare {
  id: string;
  name: string | null;
  updatedAt: number;
}

export interface UISlice {
  tab: EditorTab;
  payloadsOpen: boolean;
  copied: boolean;
  unreadCaptures: number;
  notifyOnCapture: boolean;
  helpOpen: boolean;
  previewOpen: boolean;
  submitOpen: boolean;
  paletteOpen: boolean;
  currentShare: CurrentShare | null;
  // Editor state as of the last save or load of the open share. Compared
  // against the live state to show the unsaved dot and quiet the Save button.
  savedState: EditorState | null;
  setTab: (tab: EditorTab) => void;
  setPayloadsOpen: (open: boolean) => void;
  setCopied: (copied: boolean) => void;
  bumpUnreadCaptures: () => void;
  resetUnreadCaptures: () => void;
  setNotifyOnCapture: (on: boolean) => void;
  setHelpOpen: (open: boolean) => void;
  setPreviewOpen: (open: boolean) => void;
  setSubmitOpen: (open: boolean) => void;
  setPaletteOpen: (open: boolean) => void;
  setCurrentShare: (share: CurrentShare | null) => void;
  // Mark the live state as saved (after a successful save).
  markSaved: () => void;
}

export interface ParamsSlice {
  status: number;
  delay: number;
  chunks: number;
  chunkDelay: number;
  payloadParam: PayloadParam;
  setStatus: (n: number) => void;
  setDelay: (n: number) => void;
  setChunks: (n: number) => void;
  setChunkDelay: (n: number) => void;
  setPayloadParam: (p: PayloadParam) => void;
}

export interface BodySlice {
  editorLanguage: EditorLanguage;
  editorContent: string;
  setEditorLanguage: (lang: EditorLanguage) => void;
  setEditorContent: (content: string) => void;
}

export interface HeadersSlice {
  headers: HeaderRow[];
  addHeader: (init?: Partial<Omit<HeaderRow, 'id'>>) => void;
  updateHeader: (id: string, patch: Partial<HeaderRow>) => void;
  removeHeader: (id: string) => void;
  clearHeaders: () => void;
  setHeaders: (rows: HeaderRow[]) => void;
}

export interface DomainSlice {
  domainBase: string;
  domainSub: string;
  availableDomains: string[];
  repoUrl: string | null;
  setDomainBase: (base: string) => void;
  setDomainSub: (sub: string) => void;
  setAvailableDomains: (domains: string[]) => void;
  setRepoUrl: (url: string | null) => void;
}

export interface UndoSnapshot {
  state: EditorState;
  share: CurrentShare | null;
  // Full sentence shown in the toast, e.g. 'Applied "CSP: block inline"'.
  message: string;
}

export interface AppActions {
  applyPreset: (preset: EditorState, opts?: { label?: string }) => void;
  // "New": clear to the default document and detach from any open share.
  // Undoable from the toast, so no confirm dialog.
  newDocument: () => void;
  loadState: (state: EditorState) => void;
  undoLastApply: () => void;
  dismissUndo: () => void;
}

export interface UndoSlice {
  undoSnapshot: UndoSnapshot | null;
}

export type AppState =
  UISlice & ParamsSlice & BodySlice & HeadersSlice & DomainSlice & UndoSlice & AppActions;

function rowId(): string {
  return Math.random().toString(36).slice(2, 10);
}

const initial = (): EditorState => ({ ...defaultEditorState(), ...loadFromStorage() });

function initialShare(): CurrentShare | null {
  const meta: HydratedShare | null = loadHydratedShare();
  if (!meta || !meta.owned) return null;
  return { id: meta.id, name: meta.name, updatedAt: meta.updatedAt };
}

function initialDomain(): { base: string; sub: string } {
  const persisted = typeof window !== 'undefined' ? loadDomainFromStorage() : null;
  if (persisted) return persisted;
  const fallbackBase =
    typeof window !== 'undefined' && window.location.hostname && window.location.hostname !== 'localhost'
      ? window.location.hostname.replace(/^[^.]+\.(?=[^.]+\.)/, '') // drop leading sub if multi-level
      : 'xss.cool';
  return { base: fallbackBase, sub: '' };
}

function stateFields(state: EditorState) {
  return {
    status: state.status,
    delay: state.delay,
    chunks: state.chunks ?? 1,
    chunkDelay: state.chunkDelay ?? 0,
    payloadParam: state.payloadParam,
    editorLanguage: state.editorLanguage,
    editorContent: state.editorContent,
    headers: state.headers.map((h) => ({
      id: h.id ?? rowId(),
      name: h.name,
      value: h.value,
    })),
  };
}

function readFlag(key: string): boolean {
  try {
    return typeof localStorage !== 'undefined' && localStorage.getItem(key) === '1';
  } catch {
    return false;
  }
}

function writeFlag(key: string, on: boolean): void {
  try {
    if (typeof localStorage !== 'undefined') localStorage.setItem(key, on ? '1' : '0');
  } catch {
    /* ignore */
  }
}

export const useStore = create<AppState>()((set, get) => {
  const seed = initial();
  const domainSeed = initialDomain();
  return {
    // ui
    tab: loadTabFromStorage() ?? 'body',
    payloadsOpen: false,
    copied: false,
    unreadCaptures: 0,
    notifyOnCapture: readFlag('xsscool:notify'),
    helpOpen: false,
    previewOpen: readFlag('xsscool:preview'),
    submitOpen: false,
    paletteOpen: false,
    currentShare: initialShare(),
    savedState: initialShare() ? seed : null,
    setTab: (tab) => set((s) => ({ tab, unreadCaptures: tab === 'captures' ? 0 : s.unreadCaptures })),
    setPayloadsOpen: (payloadsOpen) => set({ payloadsOpen }),
    setCopied: (copied) => set({ copied }),
    bumpUnreadCaptures: () =>
      set((s) => (s.tab === 'captures' ? s : { unreadCaptures: s.unreadCaptures + 1 })),
    resetUnreadCaptures: () => set({ unreadCaptures: 0 }),
    setNotifyOnCapture: (on) => {
      writeFlag('xsscool:notify', on);
      set({ notifyOnCapture: on });
    },
    setHelpOpen: (helpOpen) => set({ helpOpen }),
    setPreviewOpen: (previewOpen) => {
      writeFlag('xsscool:preview', previewOpen);
      set({ previewOpen });
    },
    setSubmitOpen: (submitOpen) => set({ submitOpen }),
    setPaletteOpen: (paletteOpen) => set({ paletteOpen }),
    setCurrentShare: (currentShare) => set({ currentShare, savedState: currentShare ? selectEditorState(get()) : null }),
    markSaved: () => set({ savedState: selectEditorState(get()) }),

    // params
    status: seed.status,
    delay: seed.delay,
    chunks: seed.chunks ?? 1,
    chunkDelay: seed.chunkDelay ?? 0,
    payloadParam: seed.payloadParam,
    setStatus: (status) => set({ status }),
    setDelay: (delay) => set({ delay }),
    setChunks: (chunks) => set({ chunks }),
    setChunkDelay: (chunkDelay) => set({ chunkDelay }),
    // The editor language follows the body type; there is no separate control.
    setPayloadParam: (payloadParam) =>
      set({ payloadParam, editorLanguage: languageForParam(payloadParam) }),

    // body
    editorLanguage: seed.editorLanguage,
    editorContent: seed.editorContent,
    setEditorLanguage: (editorLanguage) => set({ editorLanguage }),
    setEditorContent: (editorContent) => set({ editorContent }),

    // domain
    domainBase: domainSeed.base,
    domainSub: domainSeed.sub,
    availableDomains: [domainSeed.base],
    repoUrl: null,
    setDomainBase: (domainBase) => {
      saveDomainToStorage({ base: domainBase, sub: get().domainSub });
      set({ domainBase });
    },
    setDomainSub: (domainSub) => {
      saveDomainToStorage({ base: get().domainBase, sub: domainSub });
      set({ domainSub });
    },
    setAvailableDomains: (availableDomains) => set({ availableDomains }),
    setRepoUrl: (repoUrl) => set({ repoUrl }),

    // headers
    headers: seed.headers,
    addHeader: (init) =>
      set((s) => ({
        headers: [
          ...s.headers,
          { id: rowId(), name: init?.name ?? '', value: init?.value ?? '' },
        ],
      })),
    updateHeader: (id, patch) =>
      set((s) => ({
        headers: s.headers.map((h) => (h.id === id ? { ...h, ...patch } : h)),
      })),
    removeHeader: (id) =>
      set((s) => ({ headers: s.headers.filter((h) => h.id !== id) })),
    clearHeaders: () => set({ headers: [] }),
    setHeaders: (headers) => set({ headers }),

    // undo
    undoSnapshot: null,

    // actions
    applyPreset: (preset, opts) => {
      const prev = selectEditorState(get());
      set({
        undoSnapshot: {
          state: prev,
          share: get().currentShare,
          message: opts?.label ? `Applied "${opts.label}"` : 'Applied preset',
        },
        ...stateFields(preset),
      });
    },
    newDocument: () => {
      const prev = selectEditorState(get());
      const prevShare = get().currentShare;
      set({
        undoSnapshot: { state: prev, share: prevShare, message: 'Editor cleared' },
        currentShare: null,
        savedState: null,
        ...stateFields(defaultEditorState()),
      });
      if (typeof history !== 'undefined' && prevShare) history.replaceState(null, '', '/');
    },
    loadState: (state) => set({ ...stateFields(state) }),
    undoLastApply: () => {
      const snap = get().undoSnapshot;
      if (!snap) return;
      // Single-step undo, not a redo stack: undoing an undo doesn't re-apply.
      set({
        undoSnapshot: null,
        currentShare: snap.share,
        savedState: snap.share ? snap.state : null,
        ...stateFields(snap.state),
      });
      if (typeof history !== 'undefined') {
        history.replaceState(null, '', snap.share ? `/e/${snap.share.id}` : '/');
      }
    },
    dismissUndo: () => set({ undoSnapshot: null }),
  };
});

export function selectEditorState(s: AppState): EditorState {
  return {
    status: s.status,
    delay: s.delay,
    chunks: s.chunks,
    chunkDelay: s.chunkDelay,
    payloadParam: s.payloadParam,
    editorLanguage: s.editorLanguage,
    editorContent: s.editorContent,
    headers: s.headers,
  };
}

// True when an open share has edits that differ from its last saved state.
export function selectDirty(s: AppState): boolean {
  if (!s.currentShare || !s.savedState) return false;
  return JSON.stringify(selectEditorState(s)) !== JSON.stringify(s.savedState);
}
